/**
 * Image CDN Configuration
 *
 * Shared constants and types for Image CDN (CloudFront + Lambda@Edge)
 * Used by backend Lambda handler and frontend SDKs.
 *
 * @see documentation/api-docs/image-cdn.md
 */
// ============================================
// CONSTANTS
// ============================================
/** Maximum output dimension when no preset specified */
export const IMAGE_CDN_MAX_DIMENSION = 2048;
/** Default quality percentage */
export const IMAGE_CDN_DEFAULT_QUALITY = 80;
/** Default fit mode */
export const IMAGE_CDN_DEFAULT_FIT = 'inside';
/** Default format (auto-detect based on browser Accept header) */
export const IMAGE_CDN_DEFAULT_FORMAT = 'auto';
/** Available presets with their dimensions */
export const IMAGE_CDN_PRESETS = {
    thumb: { width: 150, height: 150 },
    medium: { width: 600, height: 600 },
    large: { width: 1200, height: 1200 },
    hero: { width: 1920, height: 800 },
    avatar: { width: 200, height: 200 },
    card: { width: 400, height: 300 },
};
/** Supported input/output formats */
export const IMAGE_CDN_SUPPORTED_FORMATS = ['jpeg', 'jpg', 'png', 'webp', 'gif', 'avif'];
/** Cache TTL in seconds (1 year) */
export const IMAGE_CDN_CACHE_TTL = 31536000;
/** Maximum input file size in bytes (10MB) */
export const IMAGE_CDN_MAX_INPUT_SIZE = 10 * 1024 * 1024;
// ============================================
// HELPER FUNCTIONS
// ============================================
/**
 * Build CDN URL with options
 *
 * @example
 * buildImageCdnUrl('https://cdn.example.com/photo.jpg', { preset: 'thumb' })
 * // => 'https://cdn.example.com/photo.jpg?preset=thumb'
 *
 * buildImageCdnUrl('https://cdn.example.com/photo.jpg', { preset: 'large', q: 95 })
 * // => 'https://cdn.example.com/photo.jpg?preset=large&q=95'
 */
export function buildImageCdnUrl(baseUrl, options = {}) {
    const params = new URLSearchParams();
    if (options.preset)
        params.set('preset', options.preset);
    if (options.q !== undefined)
        params.set('q', options.q.toString());
    if (options.f)
        params.set('f', options.f);
    if (options.fit)
        params.set('fit', options.fit);
    const query = params.toString();
    return query ? `${baseUrl}?${query}` : baseUrl;
}
/**
 * Check if a preset name is valid
 */
export function isValidPreset(preset) {
    return preset in IMAGE_CDN_PRESETS;
}
/**
 * Get dimensions for a preset
 */
export function getPresetDimensions(preset) {
    return IMAGE_CDN_PRESETS[preset];
}
/**
 * Check if a file extension is a supported image format
 */
export function isSupportedImageFormat(extension) {
    return IMAGE_CDN_SUPPORTED_FORMATS.includes(extension.toLowerCase());
}
//# sourceMappingURL=image-cdn.constants.js.map